import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { dotClass } from "@/features/command/ui";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpDown, Building2, Search } from "lucide-react";
import { Avatar, Badge, EmptyState, Skeleton, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { backFromState } from "@/lib/back-target";
import { formatDateTime, formatRelative } from "@/lib/format";
import {
  PLAN_COLORS, STAGES, STAGE_BY_KEY, eventLabel, tagColor, money, lifetimeTotal, isNearTrial,
} from "@/features/accounts/platform-meta";
import { trackRecent } from "@/lib/recent";
import { QuietPanel } from "@/features/accounts/QuietPanel";

/**
 * Accounts — the customer command center for SyncSuite PLATFORM sub-accounts,
 * fed by the platform → CRM activity pipeline (backend:
 * routes/platform-activity.ts ingest + nightly /sync reconciliation,
 * routes/platform-accounts.ts reads).
 *
 * 2026-08-22 overhaul: every row now carries its funnel STAGE (signed up →
 * verified → built → published → card → customer), journey milestone dates,
 * and money state (lifetime spend, wallet). The list is segmentable by
 * stage/plan, sortable, and the detail view renders the journey strip with
 * day-gaps — "how long from free to Pro" is a column here, not a guess.
 */

interface PlatformAccount {
  id: string;
  slug: string | null;
  name: string | null;
  plan_code: string | null;
  owner_type: string | null;
  status: string | null;
  lifecycle_state?: string | null;
  is_agency_client: boolean;
  owner_is_agency?: boolean;
  owner_email: string | null;
  owner_name: string | null;
  owner_phone?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  tags: string[];
  counters: Record<string, unknown>;
  plan_history?: PlanHop[];
  platform_created_at: string | null;
  first_seen_at: string | null;
  last_active_at: string | null;
  email_verified_at?: string | null;
  phone_verified_at?: string | null;
  first_project_at?: string | null;
  first_ai_at?: string | null;
  first_domain_live_at?: string | null;
  auto_topup_enabled?: boolean | null;
  subscription_status?: string | null;
  trial_ends_at?: string | null;
  trial_started_at?: string | null;
  revenue_usd?: string | null;
  first_paid_at?: string | null;
  first_published_at?: string | null;
  card_added_at?: string | null;
  upgraded_at?: string | null;
  canceled_at?: string | null;
  deleted_at?: string | null;
  last_synced_at?: string | null;
  lifetime_spend_usd?: string | null;
  wallet_balance_usd?: string | null;
  stage?: string;
  days_in_stage?: number | null;
  event_count?: number;
  owner_contact_id?: string | null;
  /** Command Center: the traffic source the owner is credited to. */
  source_name?: string | null;
  source_slug?: string | null;
  source_color?: string | null;
}

interface PlanHop {
  at: string;
  from: string | null;
  to: string | null;
  reason: string | null;
}

interface AccountContact {
  id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface AccountEvent {
  id: string;
  contact_id: string | null;
  event: string;
  meta: Record<string, unknown>;
  at: string;
}

function accountDisplayName(a: PlatformAccount): string {
  return a.name || a.slug || a.owner_email || a.id.slice(0, 8);
}

/** Trial vs paying, derived from the Stripe subscription status the nightly
 *  sync mirrors. Only meaningful on paid plans. */
function payState(a: PlatformAccount): { label: string; color: string } | null {
  if (!a.plan_code || a.plan_code === "free") return null;
  if (a.subscription_status === "trialing") {
    if (!isNearTrial(a.trial_ends_at)) return { label: "Beta", color: "zinc" };
    const ends = shortDate(a.trial_ends_at);
    return { label: ends ? `Trial · ends ${ends}` : "Trial", color: "amber" };
  }
  if (a.subscription_status === "past_due" || a.subscription_status === "unpaid") {
    return { label: "Past due", color: "red" };
  }
  if (a.subscription_status === "canceled") return { label: "Canceled", color: "red" };
  if (Number(a.revenue_usd) > 0) return { label: "Paying", color: "emerald" };
  if (a.subscription_status === "active") return { label: "Comped", color: "zinc" };
  return null;
}

function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function AccountsPage() {
  const { id } = useParams<{ id: string }>();
  if (id) return <AccountDetail id={id} />;
  return <AccountsList />;
}

// ─── List view ─────────────────────────────────────────────────────────────

function AccountsList() {
  const toast = useToast();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<PlatformAccount[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [plan, setPlan] = useState("");
  const [stage, setStage] = useState("");
  const [sort, setSort] = useState("last_active");

  useEffect(() => {
    let cancelled = false;
    // Debounce keystrokes; fire immediately for the first load / filter change.
    const t = setTimeout(async () => {
      try {
        const d = await api.get<{
          accounts: PlatformAccount[];
          total: number;
          tags: string[];
          stages: Record<string, number>;
        }>("/platform-accounts", { q, tag, plan, stage, sort, limit: 200 });
        if (cancelled) return;
        setAccounts(d.accounts);
        setTotal(d.total);
        setAllTags(d.tags);
        setStageCounts(d.stages ?? {});
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiError ? err.message : "Failed to load accounts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tag, plan, stage, sort]);

  const liveTotal = useMemo(
    () => Object.entries(stageCounts).reduce((n, [, v]) => n + v, 0),
    [stageCounts]
  );

  const sortHeader = (key: string, label: string, alignRight = false) => (
    <th className={`px-3 py-2.5 font-medium ${alignRight ? "text-right" : "text-left"}`}>
      <button
        onClick={() => setSort(sort === key && key === "created" ? "created_asc" : key)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-zinc-300 ${
          sort.startsWith(key) ? "text-zinc-300" : ""
        }`}
      >
        {label}
        <ArrowUpDown className="h-3 w-3" />
      </button>
    </th>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Accounts</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Every SyncSuite account: where they are in the funnel, what they've done, and what they've paid.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, owner, agency…"
              className="w-60 rounded-lg border border-zinc-700 bg-zinc-900 py-2 pl-9 pr-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-zinc-500 focus:outline-none"
            />
          </div>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="cursor-pointer rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 hover:border-zinc-600 focus:outline-none"
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="agency">Agency</option>
            <option value="mobileapp">Mobile App</option>
          </select>
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="cursor-pointer rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 hover:border-zinc-600 focus:outline-none"
          >
            <option value="">All tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Trials and paying customers who stopped using SyncSuite. */}
      <QuietPanel />

      {/* Funnel segment chips — live counts, click to filter. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          onClick={() => setStage("")}
          className={`rounded-full border px-3 py-1 text-xs font-medium ${
            stage === ""
              ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-200"
              : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
          }`}
        >
          All <span className="text-zinc-500">{liveTotal}</span>
        </button>
        {STAGES.filter((s) => s.key !== "deleted" || (stageCounts[s.key] ?? 0) > 0).map((s) => (
          <button
            key={s.key}
            onClick={() => setStage(stage === s.key ? "" : s.key)}
            className={`rounded-full border px-3 py-1 text-xs font-medium ${
              stage === s.key
                ? "border-indigo-400/60 bg-indigo-500/15 text-indigo-200"
                : "border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600"
            }`}
          >
            {s.label} <span className="text-zinc-500">{stageCounts[s.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
        </div>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={q || tag || plan || stage ? "No accounts match" : "No platform accounts yet"}
          description={
            q || tag || plan || stage
              ? "Try a different search or clear the filters."
              : "Platform accounts appear here automatically as the SyncSuite platform reports activity."
          }
        />
      ) : (
        <div className="cc-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/70 bg-zinc-900/40 text-xs uppercase tracking-wider text-zinc-500">
                {sortHeader("name", "Account")}
                <th className="px-3 py-2.5 text-left font-medium">Stage</th>
                <th className="px-3 py-2.5 text-left font-medium">Plan</th>
                {sortHeader("created", "Signed up")}
                {sortHeader("spend", "Spend")}
                <th className="px-3 py-2.5 text-left font-medium">Came from</th>
                <th className="px-3 py-2.5 text-left font-medium">Owner</th>
                {sortHeader("last_active", "Last active")}
                <th className="px-3 py-2.5 text-right font-medium">Events</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => {
                const st = STAGE_BY_KEY[a.stage ?? ""] ?? null;
                return (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/accounts/${a.id}`)}
                    className="group cursor-pointer border-b border-zinc-800/40 last:border-b-0 hover:bg-zinc-900/30"
                  >
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-zinc-100">{accountDisplayName(a)}</div>
                      <div className="text-xs text-zinc-500">
                        {a.slug}
                        {a.owner_is_agency && a.organization_name ? ` · ${a.organization_name}` : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      {st ? (
                        <div className="flex items-center gap-1.5">
                          <Badge color={st.color} size="xs">{st.label}</Badge>
                          {a.days_in_stage != null && a.days_in_stage > 0 && a.stage !== "deleted" && (
                            <span className="text-[11px] tabular-nums text-zinc-600">{a.days_in_stage}d</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {a.plan_code
                        ? <Badge color={PLAN_COLORS[a.plan_code] ?? "zinc"} size="xs">{a.plan_code}</Badge>
                        : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-zinc-500" title={a.platform_created_at ? formatDateTime(a.platform_created_at) : undefined}>
                      {a.platform_created_at ? formatRelative(a.platform_created_at) : "—"}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-zinc-400">
                      {lifetimeTotal(a) > 0 ? `$${lifetimeTotal(a).toFixed(2)}` : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5">
                      {a.source_name && a.source_slug ? (
                        <Link
                          to={`/traffic/${a.source_slug}`}
                          state={{ from: "/accounts" }}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-zinc-100 hover:underline"
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full", dotClass(a.source_color))} />
                          {a.source_name}
                        </Link>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-zinc-400">
                      {a.owner_email ? (
                        a.owner_contact_id ? (
                          <Link
                            to={`/contacts/${a.owner_contact_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-indigo-300 hover:text-indigo-200 hover:underline"
                          >
                            {a.owner_email}
                          </Link>
                        ) : (
                          a.owner_email
                        )
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-zinc-500" title={a.last_active_at ? formatDateTime(a.last_active_at) : undefined}>
                      {a.last_active_at ? formatRelative(a.last_active_at) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-zinc-500">
                      {(a.event_count ?? 0).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <p className="text-xs text-zinc-600">
          Showing {accounts.length.toLocaleString()} of {total.toLocaleString()} account{total === 1 ? "" : "s"}
        </p>
      )}
    </div>
  );
}

// ─── Detail view ───────────────────────────────────────────────────────────

/** The journey milestones, in funnel order, with their account columns. */
const JOURNEY: { key: keyof PlatformAccount; label: string }[] = [
  { key: "platform_created_at", label: "Signed up" },
  { key: "email_verified_at", label: "Verified" },
  { key: "first_ai_at", label: "Messaged AI" },
  { key: "first_published_at", label: "Published" },
  { key: "card_added_at", label: "Card" },
  { key: "first_domain_live_at", label: "Domain" },
  { key: "trial_started_at", label: "Trial" },
  { key: "first_paid_at", label: "Paying" },
];

function JourneyStrip({ account }: { account: PlatformAccount }) {
  const steps = JOURNEY.map((j) => ({
    ...j,
    at: (account[j.key] as string | null | undefined) ?? null,
  }));
  // Day-gaps between consecutive REACHED milestones.
  let prev: Date | null = null;
  const rendered = steps.map((s) => {
    const d = s.at ? new Date(s.at) : null;
    const valid = d && !Number.isNaN(d.getTime()) ? d : null;
    let gapDays: number | null = null;
    if (valid && prev) {
      gapDays = Math.max(0, Math.round((valid.getTime() - prev.getTime()) / 86_400_000));
    }
    if (valid) prev = valid;
    // In-between states that deserve their own look:
    //  - Domain added but DNS not verified yet -> amber hollow
    //  - Trial started and still running -> amber solid
    const domainPending =
      s.key === "first_domain_live_at" && !valid && account.tags.includes("Added Domain");
    const trialActive =
      s.key === "trial_started_at" && !!valid &&
      account.subscription_status === "trialing" && isNearTrial(account.trial_ends_at);
    // Running trial: the actionable date is when it ENDS, not when it began.
    const trialEnds =
      trialActive && account.trial_ends_at ? shortDate(account.trial_ends_at) : null;
    return { ...s, reached: !!valid, gapDays, domainPending, trialActive, trialEnds };
  });
  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-max items-center gap-0 pt-1">
        {rendered.map((s, i) => (
          <div key={s.key as string} className="flex items-center">
            {i > 0 && (
              <div className="relative mx-1 h-px w-10 bg-zinc-700 sm:w-14">
                {s.gapDays != null && s.gapDays > 0 && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-zinc-500">
                    {s.gapDays}d
                  </span>
                )}
              </div>
            )}
            <div className="flex flex-col items-center gap-1">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  s.trialActive
                    ? "bg-amber-400"
                    : s.reached
                      ? "bg-indigo-400"
                      : s.domainPending
                        ? "border-2 border-amber-400 bg-transparent"
                        : "border border-zinc-600 bg-transparent"
                }`}
              />
              <span className={`text-[11px] ${
                s.reached ? "text-zinc-200" : s.domainPending ? "text-amber-300" : "text-zinc-600"
              }`}>
                {s.label}
              </span>
              <span className={`text-[10px] ${s.trialEnds || s.domainPending ? "text-amber-400/80" : "text-zinc-500"}`}>
                {s.trialEnds
                  ? `ends ${s.trialEnds}`
                  : s.at
                    ? shortDate(s.at)
                    : s.domainPending
                      ? "added, not verified"
                      : ""}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccountDetail({ id }: { id: string }) {
  const toast = useToast();
  const location = useLocation();
  const back = backFromState(location.state, { to: "/accounts", label: "All accounts" });
  const [account, setAccount] = useState<PlatformAccount | null>(null);
  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [events, setEvents] = useState<AccountEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await api.get<{
          account: PlatformAccount;
          contacts: AccountContact[];
          events: AccountEvent[];
          eventsTotal: number;
        }>(`/platform-accounts/${id}`);
        if (cancelled) return;
        setAccount(d.account);
        trackRecent({ to: `/accounts/${id}`, label: d.account.name || d.account.owner_name || d.account.slug || "Account", kind: "account" });
        setContacts(d.contacts);
        setEvents(d.events);
        setEventsTotal(d.eventsTotal ?? d.events.length);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 400)) setNotFound(true);
        else toast.error(err instanceof ApiError ? err.message : "Failed to load account");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadMoreEvents = async () => {
    setLoadingMore(true);
    try {
      const d = await api.get<{ events: AccountEvent[]; eventsTotal: number }>(
        `/platform-accounts/${id}`,
        { events_offset: events.length, events_limit: 50 },
      );
      setEvents((cur) => [...cur, ...d.events]);
      setEventsTotal(d.eventsTotal ?? eventsTotal);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load more events");
    } finally {
      setLoadingMore(false);
    }
  };

  const counters = account
    ? Object.entries(account.counters)
        .filter((e): e is [string, number] => e[0].endsWith("_count") && typeof e[1] === "number")
        .sort((a, b) => b[1] - a[1])
    : [];
  const planHistory = (account?.plan_history ?? []).slice().reverse();
  const st = account?.stage ? STAGE_BY_KEY[account.stage] : null;

  return (
    <div className="space-y-5">
      <Link to={back.to} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200">
        <ArrowLeft className="h-4 w-4" /> {back.label === "All accounts" ? "All accounts" : `Back to ${back.label.toLowerCase()}`}
      </Link>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : notFound || !account ? (
        <EmptyState icon={Building2} title="Account not found" description="This platform account doesn't exist (or was removed)." />
      ) : (
        <>
          {/* Header */}
          <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-semibold text-zinc-100">{accountDisplayName(account)}</h1>
                  {st && <Badge color={st.color} size="xs">{st.label}</Badge>}
                  {(() => {
                    const ps = payState(account);
                    if (!ps || ps.label === st?.label) return null;
                    return <Badge color={ps.color} size="xs">{ps.label}</Badge>;
                  })()}
                  {account.deleted_at && <Badge color="red" size="xs">deleted</Badge>}
                </div>
                <p className="mt-0.5 text-sm text-zinc-500">
                  {account.slug && <span className="font-mono">{account.slug}</span>}
                  {account.owner_type === "agency_org" ? (
                    <span>Agency organization · bills at the organization level</span>
                  ) : (
                    <>
                      {account.organization_name && <span> · {account.organization_name}</span>}
                      {account.owner_type && <span> · {account.owner_type}</span>}
                      {account.owner_is_agency && <span> · agency-owned</span>}
                    </>
                  )}
                  {account.auto_topup_enabled === true && <span className="text-emerald-400"> · auto top-up on</span>}
                  {account.auto_topup_enabled === false && account.card_added_at && <span className="text-amber-400"> · auto top-up off</span>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {lifetimeTotal(account) > 0 && (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-right"
                       title={`Subscriptions $${(Number(account.revenue_usd) || 0).toFixed(2)} + top-ups $${(Number(account.lifetime_spend_usd) || 0).toFixed(2)}`}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Lifetime revenue</div>
                    <div className="text-sm font-semibold tabular-nums text-emerald-300">${lifetimeTotal(account).toFixed(2)}</div>
                  </div>
                )}
                {money(account.wallet_balance_usd) && (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-right">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Wallet</div>
                    <div className="text-sm font-semibold tabular-nums text-zinc-200">{money(account.wallet_balance_usd)}</div>
                  </div>
                )}
                {account.plan_code && (
                  <Badge color={PLAN_COLORS[account.plan_code] ?? "zinc"}>{account.plan_code}</Badge>
                )}
              </div>
            </div>

            {/* Journey strip — the whole point of this page. */}
            <div className="mt-4 border-t border-zinc-800/60 pt-4">
              <JourneyStrip account={account} />
            </div>

            {account.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {account.tags.map((t) => <Badge key={t} color={tagColor(t)} size="xs">{t}</Badge>)}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-zinc-500 sm:grid-cols-4">
              <div>Created {account.platform_created_at ? formatRelative(account.platform_created_at) : "—"}</div>
              {account.owner_type !== "agency_org" && (
                <div>Last active {account.last_active_at ? formatRelative(account.last_active_at) : "—"}</div>
              )}
              <div>Synced {account.last_synced_at ? formatRelative(account.last_synced_at) : "—"}</div>
              <div className="truncate" title={account.id}>ID {account.id.slice(0, 8)}…</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="space-y-5">
              {/* People */}
              <section className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
                <h2 className="text-sm font-semibold text-zinc-200">People</h2>
                {contacts.length === 0 ? (
                  <p className="mt-2 text-sm text-zinc-500">No linked contacts yet.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {contacts.map((c) => {
                      const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email || "Unknown";
                      return (
                        <li key={c.id}>
                          <Link
                            to={`/contacts/${c.id}`}
                            state={{ from: `/accounts/${id}` }}
                            className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-900/60"
                          >
                            <Avatar name={name} email={c.email} src={c.avatar_url} size="sm" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm text-zinc-100">{name}</span>
                              {c.email && <span className="block truncate text-xs text-zinc-500">{c.email}</span>}
                            </span>
                            <Badge color={c.role === "owner" ? "indigo" : "zinc"} size="xs">{c.role}</Badge>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Plan history */}
              {planHistory.length > 0 && (
                <section className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
                  <h2 className="text-sm font-semibold text-zinc-200">Plan history</h2>
                  <ul className="mt-2 space-y-1.5">
                    {planHistory.map((h, i) => (
                      <li key={i} className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="text-zinc-300">
                          {h.from ?? "—"} → <span className="text-zinc-100">{h.to ?? "—"}</span>
                          {h.reason && <span className="ml-1 text-[11px] text-zinc-600">{h.reason.replaceAll("_", " ")}</span>}
                        </span>
                        <span className="shrink-0 text-xs text-zinc-500" title={formatDateTime(h.at)}>
                          {formatRelative(h.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* What they've set up — plan-aware; workspace-only concepts,
                  so hidden entirely for agency organizations. */}
              {account.owner_type !== "agency_org" && (
                <section className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
                  <h2 className="text-sm font-semibold text-zinc-200">Set up</h2>
                  <SetupChecklist account={account} />
                </section>
              )}

              {/* Counters — event-driven, so org rows (nightly-sync only)
                  never have any; hide the empty card. */}
              {account.owner_type !== "agency_org" && (
              <section className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
                <h2 className="text-sm font-semibold text-zinc-200">Usage</h2>
                {counters.length === 0 ? (
                  <p className="mt-2 text-sm text-zinc-500">No activity counted yet.</p>
                ) : (
                  <dl className="mt-2 space-y-1">
                    {counters.map(([key, value]) => (
                      <div key={key} className="flex items-baseline justify-between gap-2 text-sm">
                        <dt className="text-zinc-400">{eventLabel(key.slice(0, -"_count".length))}</dt>
                        <dd className="tabular-nums text-zinc-200">{value.toLocaleString()}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </section>
              )}
            </div>

            {/* Timeline — org rows are nightly-sync summaries with no event
                feed, so the card would always be empty; hide it. */}
            {account.owner_type !== "agency_org" && (
            <section className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4 lg:col-span-2">
              <h2 className="text-sm font-semibold text-zinc-200">
                Activity
                {eventsTotal > 0 && <span className="ml-1.5 text-xs font-normal text-zinc-500">{eventsTotal.toLocaleString()}</span>}
              </h2>
              {events.length === 0 ? (
                <p className="mt-2 text-sm text-zinc-500">No events recorded yet.</p>
              ) : (
                <ul className="mt-3 space-y-3">
                  {events.map((e) => {
                    const metaEntries = Object.entries(e.meta ?? {});
                    return (
                      <li key={e.id} className="flex gap-3">
                        <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-indigo-400/70" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                            <span className="text-sm text-zinc-100">{eventLabel(e.event)}</span>
                            <span className="text-xs text-zinc-500" title={formatDateTime(e.at)}>
                              {formatRelative(e.at)}
                            </span>
                          </div>
                          {metaEntries.length > 0 && (
                            <p className="mt-0.5 truncate font-mono text-[11px] text-zinc-500" title={JSON.stringify(e.meta)}>
                              {metaEntries.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ")}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {events.length < eventsTotal && (
                <button
                  onClick={loadMoreEvents}
                  disabled={loadingMore}
                  className="mt-3 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : `Show more (${(eventsTotal - events.length).toLocaleString()} older)`}
                </button>
              )}
            </section>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ─── Plan-aware setup checklist ────────────────────────────────────────────

const PAID_PLANS = new Set(["pro", "agency", "mobileapp"]);

function SetupChecklist({ account }: { account: PlatformAccount }) {
  const has = (tag: string) => account.tags.includes(tag);
  const isPaid = PAID_PLANS.has(account.plan_code ?? "");
  const rows: { label: string; done: boolean; proOnly: boolean; extra?: string }[] = [
    { label: "Custom domain live", done: !!account.first_domain_live_at, proOnly: false },
    { label: "Bought a domain", done: has("Bought Domain"), proOnly: false },
    { label: "Team member added", done: has("Has Team"), proOnly: false },
    { label: "Auto top-up", done: account.auto_topup_enabled === true, proOnly: false,
      extra: account.auto_topup_enabled === false && account.card_added_at ? "off" : undefined },
    { label: "Email sending connected", done: has("Email Connected") || has("Email Domain Verified"), proOnly: true },
    { label: "Phone number", done: has("Has Phone Number"), proOnly: true },
    { label: "A2P texting registered", done: has("A2P Submitted"), proOnly: true },
  ];
  return (
    <ul className="mt-2 space-y-1.5">
      {rows.map((r) => {
        const locked = r.proOnly && !isPaid;
        return (
          <li key={r.label} className="flex items-center gap-2 text-sm">
            {locked ? (
              <span className="flex h-4 w-4 items-center justify-center rounded border border-zinc-800 text-[9px] text-zinc-600">–</span>
            ) : r.done ? (
              <span className="flex h-4 w-4 items-center justify-center rounded bg-emerald-500/20 text-[10px] text-emerald-300">✓</span>
            ) : (
              <span className="h-4 w-4 rounded border border-zinc-700" />
            )}
            <span className={locked ? "text-zinc-600" : r.done ? "text-zinc-200" : "text-zinc-400"}>
              {r.label}
              {r.extra && <span className="ml-1.5 text-xs text-amber-400">{r.extra}</span>}
            </span>
            {locked && <Badge color="zinc" size="xs">Pro</Badge>}
          </li>
        );
      })}
    </ul>
  );
}
